import { Avatar, Box, Button, Chip, makeStyles, Typography } from '@material-ui/core';
import { Description, GitHub, Camera } from '@material-ui/icons';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';

import peter from '../images/authors/peter.jpg';
import linjie from '../images/authors/linjie.jpg';
import imran from '../images/authors/imran.jpg';
import soumyadip from '../images/authors/soumyadip.jpg';


const useStyles = makeStyles((theme) => ({
  items: {
    textAlign: 'center',
    padding: theme.spacing(3, 0),
    '& > *': {
      margin: theme.spacing(0.5)
    }
  },
  content: {
    '& > h6': {
      marginTop: theme.spacing(6)
    }
  },
  video: {
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('xs')]: {
      height: '250px !important'
    }
  },
  video1Column: {
    height: 450
  },
  video2Column: {
    height: 200
  },
  video3Column: {
    height: 135
  },
  citation: {
    whiteSpace: 'pre-wrap'
  }
}));

const ProjectPage: FC = () => {
  const classes = useStyles();

  return (
    <>
      <Helmet>
        <title>RVM: Robust High-Resolution Video Matting with Temporal Guidance</title>
        <meta name="description" content="Official site for paper Robust High-Resolution Video Matting with Temporal Guidance" />
      </Helmet>
      <Box px={2} pt={8} pb={4}>
        <Typography variant="h4" align="center" gutterBottom>Robust High-Resolution Video Matting with Temporal Guidance</Typography>
        <Typography variant="h6" align="center" gutterBottom>Human Matting on Any Videos | Real-time <strong>4K 70FPS+</strong> and <strong>HD 100FPS+</strong></Typography>
        <Typography color="textSecondary" align="center">ByteDance Inc. | University of Washington</Typography>
        
        <div className={classes.items}>
          <Chip
            component="a"
            href="https://www.linkedin.com/in/shanchuanlin"
            target="_blank"
            clickable
            avatar={<Avatar src={peter}/>}
            label="Shanchuan Lin" />
          <Chip
            component="a"
            href="https://sites.google.com/site/linjieyang89/"
            target="_blank"
            clickable
            avatar={<Avatar src={linjie}/>}
            label="Linjie Yang" />
          <Chip
            component="a"
            href="https://www.linkedin.com/in/imran-saleemi/"
            target="_blank"
            clickable
            avatar={<Avatar src={imran}/>}
            label="Imran Saleemi" />
          <Chip
            component="a"
            href="https://homes.cs.washington.edu/~soumya91/"
            target="_blank"
            clickable
            avatar={<Avatar src={soumyadip}/>}
            label="Soumyadip Sengupta" />
        </div>

        <div className={classes.items}>
          <Button color="primary" startIcon={<Description/>} component="a" href="http://arxiv.org/abs/2108.11515" target="_blank">Paper</Button>
          <Button color="primary" startIcon={<GitHub/>} component="a" href="https://github.com/PeterL1n/RobustVideoMatting" target="_blank">Code</Button>
          <Button color="primary" startIcon={<Camera/>} component={Link} to="/demo">Demo</Button>
        </div>
      </Box>

      <Box p={2} mb={4} maxWidth={800} m="0 auto" className={classes.content}>
        <iframe
          className={classNames(classes.video, classes.video1Column)}
          src="https://www.youtube.com/embed/Jvzltozpbpk?rel=0&modestbranding=1&autohide=1&showinfo=0&enablejsapi=1"
          title="YouTube video player"
          frameBorder={0}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          width="100%" />
        <Typography variant="caption" align="center" component="div">Open in <a href="https://youtu.be/Jvzltozpbpk" target="_blank">YouTube</a> or <a href="https://www.bilibili.com/video/BV1Z3411B7g7/" target="_blank">Bilibili</a>.</Typography>

        <Typography variant="h6" gutterBottom>Abstract</Typography>
        <Typography paragraph>
          We introduce a robust, real-time, high-resolution human video matting method that achieves new state-of-the-art performance. Our method is much lighter than previous approaches and can process 4K at 76 FPS and HD at 104 FPS on an Nvidia GTX 1080Ti GPU. Unlike most existing methods that perform video matting frame-by-frame as independent images, our method uses a recurrent architecture to exploit temporal information in videos and achieves significant improvements in temporal coherence and matting quality. Furthermore, we propose a novel training strategy that enforces our network on both matting and segmentation objectives. This significantly improves our model's robustness. Our method does not require any auxiliary inputs such as a trimap or a pre-captured background image, so it can be widely applied to existing human matting applications.
        </Typography>

        <Typography variant="h6" gutterBottom>Download</Typography>
        <Typography paragraph>
          Our model is available in <b>PyTorch</b>, <b>TorchScript</b>, <b>ONNX</b>, <b>TensorFlow</b>, and <b>TensorFlow.js</b>.
        </Typography>
        <Box textAlign="center">
          <Button color="primary" startIcon={<GitHub/>} component="a" href="https://github.com/PeterL1n/RobustVideoMatting" target="_blank">Github</Button>
        </Box>

        <Typography variant="h6" gutterBottom>Supplementary</Typography>
        <Typography gutterBottom>We show the recurrent states of our model in the following video. Our model learns to reconstruct the background and keep track of the motion history.</Typography>
        <iframe
          className={classNames(classes.video, classes.video1Column)}
          src="https://www.youtube.com/embed/Ay-mGCEYEzM?rel=0&modestbranding=1&autohide=1&showinfo=0&enablejsapi=1"
          title="YouTube video player"
          frameBorder={0}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          width="100%" />
        <Typography variant="caption" align="center" component="div">Open in <a href="https://youtu.be/Ay-mGCEYEzM" target="_blank">YouTube</a> or <a href="https://www.bilibili.com/video/BV19Q4y1h7xK/" target="_blank">Bilibili</a>.</Typography>

        <Typography variant="h6" gutterBottom>Citation</Typography>
        <code className={classes.citation}>
          {
`@misc{rvm,
      title={Robust High-Resolution Video Matting with Temporal Guidance}, 
      author={Shanchuan Lin and Linjie Yang and Imran Saleemi and Soumyadip Sengupta},
      year={2021},
      eprint={2108.11515},
      archivePrefix={arXiv},
      primaryClass={cs.CV}
}`
          }
        </code>
      </Box>
    </>
  )
};

export default ProjectPage;